import type { ListResponse } from 'Services/SalesManagement/types'
import type { LoadingStates } from 'Store'
import {
  createAxiosAsyncThunk,
  createAxiosAsyncThunkWithParams,
  defaultAxiosOptions,
  getAxiosInstance,
  type ValidationErrors
} from 'Util/ApiClient'
import actionGenerator from 'Util/actionGenerator'
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import axios, { type AxiosError } from 'axios'

export interface ZoneInfoShape {
  id: number
  created: Date
  modified: Date
  authority_code: string
  authority_name: string
  text_fi: string
  text_sv: string
  text_en: string
}

export interface ZoneInfoDetailShape extends Omit<ZoneInfoShape, 'id' | 'created' | 'modified'> {
  id: number | null
  created: Date | null
  modified: Date | null
  image: string | null
}

export interface ZoneInfoOptions {
  authorities: []
}

export interface ZoneInfoState {
  zoneInfos: ZoneInfoShape[]
  count: number
  next: string | null
  previous: string | null
  loading: LoadingStates
  submitLoading: LoadingStates
  deleteLoading: LoadingStates
  singleZoneInfoLoading: LoadingStates
  optionsLoading: LoadingStates
  singleZoneInfo: ZoneInfoDetailShape | null
  zoneInfoOptions: ZoneInfoOptions
}

const initialState: ZoneInfoState = {
  zoneInfos: [],
  count: 0,
  next: null,
  previous: null,
  loading: 'idle',
  submitLoading: 'idle',
  deleteLoading: 'idle',
  singleZoneInfoLoading: 'idle',
  optionsLoading: 'idle',
  singleZoneInfo: null,
  zoneInfoOptions: {
    authorities: []
  }
}

export interface ZoneInfoParamShape {
  page: number
  ordering: string
}

type FetchZoneInfoResponse = ZoneInfoDetailShape

export interface ZoneInfoRequest {
  authority_code: string
}

const fetchZoneInfo = createAsyncThunk<
  FetchZoneInfoResponse,
  ZoneInfoRequest,
  {
    rejectValue: ValidationErrors
  }
>('zoneInfo/fetchZoneInfo', async (data, { rejectWithValue }) => {
  try {
    const { authority_code } = data
    const response = await getAxiosInstance().get<FetchZoneInfoResponse>(
      `/api/v1/zone-info/${authority_code}/`
    )
    return response.data
  } catch (err) {
    if (axios.isAxiosError(err)) {
      const error: AxiosError<ValidationErrors> = err // cast the error for access
      if (!error.response) {
        throw err
      }
      return rejectWithValue({ ...error.response.data, status: error.response.status })
    }
    throw err
  }
})

type FetchZoneInfoListParams = ZoneInfoParamShape

const fetchZoneInfoList = createAxiosAsyncThunkWithParams<
  ListResponse<ZoneInfoShape>,
  FetchZoneInfoListParams | undefined
>('zoneInfo/fetchZoneInfoList', '/api/v1/zone-info/')

interface FetchZoneInfoOptions {
  field_choices: ZoneInfoOptions
}

const fetchZoneInfoOptions = createAxiosAsyncThunk<FetchZoneInfoOptions, undefined>(
  'zoneInfo/fetchZoneInfoOptions',
  '/api/v1/zone-info/',
  'OPTIONS'
)

type CreateZoneInfoRequest = ZoneInfoDetailShape

type CreateZoneInfoResponse = ZoneInfoDetailShape

const createZoneInfo = createAsyncThunk<
  CreateZoneInfoResponse,
  CreateZoneInfoRequest,
  {
    rejectValue: ValidationErrors
  }
>('zoneInfo/createZoneInfo', async (data, { rejectWithValue }) => {
  try {
    const { ...fields } = data
    const response = await getAxiosInstance().post<CreateZoneInfoResponse>(
      `/api/v1/zone-info/`,
      fields
    )
    return response.data
  } catch (err) {
    if (axios.isAxiosError(err)) {
      const error: AxiosError<ValidationErrors> = err // cast the error for access
      if (!error.response) {
        throw err
      }
      return rejectWithValue({ ...error.response.data, status: error.response.status })
    }
    throw err
  }
})

type ChangeZoneInfoRequest = ZoneInfoDetailShape

type ChangeZoneInfoResponse = ZoneInfoDetailShape

const changeZoneInfo = createAsyncThunk<
  ChangeZoneInfoResponse,
  ChangeZoneInfoRequest,
  {
    rejectValue: ValidationErrors
  }
>('zoneInfo/changeZoneInfo', async (data, { rejectWithValue }) => {
  try {
    const { ...fields } = data
    const response = await getAxiosInstance().put<ChangeZoneInfoResponse>(
      `/api/v1/zone-info/${fields.authority_code}/`,
      fields
    )
    return response.data
  } catch (err) {
    if (axios.isAxiosError(err)) {
      const error: AxiosError<ValidationErrors> = err
      if (!error.response) {
        throw err
      }
      return rejectWithValue({ ...error.response.data, status: error.response.status })
    }
    throw err
  }
})

type DeleteZoneInfoResponse = Record<string, never>

const deleteZoneInfo = createAsyncThunk<
  DeleteZoneInfoResponse,
  ZoneInfoRequest,
  {
    rejectValue: ValidationErrors
  }
>('zoneInfo/deleteZoneInfo', async (data, { rejectWithValue }) => {
  try {
    const { authority_code } = data
    const response = await getAxiosInstance().delete<DeleteZoneInfoResponse>(
      `/api/v1/zone-info/${authority_code}/`
    )
    return response.data
  } catch (err) {
    if (axios.isAxiosError(err)) {
      const error: AxiosError<ValidationErrors> = err // cast the error for access
      if (!error.response) {
        throw err
      }
      return rejectWithValue({ ...error.response.data, status: error.response.status })
    }
    throw err
  }
})

interface UploadZoneInfoImageRequest {
  authority_code: string
  formData: FormData
}

interface UploadZoneInfoImageResponse {
  image: string
}

const uploadZoneInfoImage = createAsyncThunk<
  UploadZoneInfoImageResponse,
  UploadZoneInfoImageRequest,
  {
    rejectValue: ValidationErrors
  }
>('zoneInfo/uploadZoneInfoImage', async (data, { rejectWithValue }) => {
  try {
    const { authority_code, formData } = data
    const response = await getAxiosInstance(
      'json',
      defaultAxiosOptions.timeout,
      'multipart/form-data'
    ).put<UploadZoneInfoImageResponse>(`/api/v1/zone-info/${authority_code}/image/`, formData)
    return response.data
  } catch (err) {
    if (axios.isAxiosError(err)) {
      const error: AxiosError<ValidationErrors> = err
      if (!error.response) {
        throw err
      }
      return rejectWithValue({ ...error.response.data, status: error.response.status })
    }
    throw err
  }
})

const genericActions = actionGenerator('zoneInfo')

const zoneInfoSlice = createSlice({
  name: genericActions.name,
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(fetchZoneInfo.fulfilled, (state, { payload }) => {
        state.singleZoneInfo = payload
        state.singleZoneInfoLoading = 'fulfilled'
      })
      .addCase(fetchZoneInfo.pending, state => {
        state.singleZoneInfo = initialState.singleZoneInfo
        state.singleZoneInfoLoading = 'pending'
      })
      .addCase(fetchZoneInfo.rejected, state => {
        state.singleZoneInfoLoading = 'rejected'
      })

      .addCase(fetchZoneInfoList.fulfilled, (state, { payload }) => {
        state.zoneInfos = payload.results
        state.count = payload.count
        state.next = payload.next
        state.previous = payload.previous
        state.loading = 'fulfilled'
      })
      .addCase(fetchZoneInfoList.pending, state => {
        state.zoneInfos = initialState.zoneInfos
        state.loading = 'pending'
      })
      .addCase(fetchZoneInfoList.rejected, state => {
        state.loading = 'rejected'
      })

      .addCase(fetchZoneInfoOptions.fulfilled, (state, { payload }) => {
        state.zoneInfoOptions = payload.field_choices
        state.optionsLoading = 'fulfilled'
      })
      .addCase(fetchZoneInfoOptions.pending, state => {
        state.zoneInfoOptions = initialState.zoneInfoOptions
        state.optionsLoading = 'pending'
      })
      .addCase(fetchZoneInfoOptions.rejected, state => {
        state.optionsLoading = 'rejected'
      })

      .addCase(createZoneInfo.fulfilled, (state, { payload }) => {
        state.singleZoneInfo = payload
        state.submitLoading = 'fulfilled'
      })
      .addCase(createZoneInfo.pending, state => {
        state.singleZoneInfo = initialState.singleZoneInfo
        state.submitLoading = 'pending'
      })
      .addCase(createZoneInfo.rejected, state => {
        state.submitLoading = 'rejected'
      })

      .addCase(changeZoneInfo.fulfilled, (state, { payload }) => {
        state.singleZoneInfo = payload
        state.submitLoading = 'fulfilled'
      })
      .addCase(changeZoneInfo.pending, state => {
        state.singleZoneInfo = initialState.singleZoneInfo
        state.submitLoading = 'pending'
      })
      .addCase(changeZoneInfo.rejected, state => {
        state.submitLoading = 'rejected'
      })

      .addCase(deleteZoneInfo.fulfilled, state => {
        state.deleteLoading = 'fulfilled'
      })
      .addCase(deleteZoneInfo.pending, state => {
        state.deleteLoading = 'pending'
      })
      .addCase(deleteZoneInfo.rejected, state => {
        state.deleteLoading = 'rejected'
      })

      .addCase(genericActions.ResetAction, () => initialState)
  }
})

export default zoneInfoSlice.reducer

export {
  fetchZoneInfoOptions,
  fetchZoneInfo,
  fetchZoneInfoList,
  deleteZoneInfo,
  changeZoneInfo,
  createZoneInfo,
  uploadZoneInfoImage
}
